import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, CreditCard, Gift, Loader2, MinusCircle } from 'lucide-react';
import { commanderModulesAdmin, fetchModulesAdmin, garderModuleAdmin } from '../services/api';
// L'icône choisie au back-office de la boutique : le miroir du catalogue du
// shop la rend ici à l'identique.
import { ICONES_MODULES as ICONES, IconeModuleParDefaut as Puzzle } from '../utils/iconesModules';

import { dateFr } from '../utils/dates';
type Module = {
  code: string;
  nom: string;
  description: string;
  icone: string;
  prixMensuel: number;
  prixAnnuel: number;
  disponible: boolean;
  souscrit: boolean;
  inclus: boolean;
  pilotable: boolean;
  /** Écarté par l'association : fermé, et hors des prochaines échéances. */
  refuse: boolean;
  /** L'association peut l'écarter — ou le reprendre si elle l'a écarté. */
  refusable: boolean;
  /** Réglé pour la période en cours : il reste ouvert jusqu'à son terme. */
  payeJusquAuTerme: boolean;
};

const euros = (montant: number) =>
  montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' €';

/**
 * Le oui/non, tel qu'il existait sur l'espace supplémentaire.
 *
 * Deux gestes distincts s'en servent — mettre au panier, et garder ou écarter
 * un module qu'on a déjà — mais c'est le même objet à l'écran, et il vaut
 * mieux qu'il le reste : deux interrupteurs de formes différentes sur la même
 * carte se liraient comme deux choses sans rapport.
 */
function Interrupteur({
  actif,
  onChange,
  occupe = false,
  libelle,
}: {
  actif: boolean;
  onChange: () => void;
  occupe?: boolean;
  libelle: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
      <span className={actif ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500'}>
        {actif ? 'Oui' : 'Non'}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={actif}
        aria-label={libelle}
        disabled={occupe}
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
          actif ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            actif ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

/**
 * Les modules optionnels de la plateforme : ce qui est inclus, ce qui se
 * souscrit. Le catalogue et les prix viennent de la boutique ; la sélection
 * se règle par Stripe, l'activation revient toute seule après paiement.
 */
export default function ModulesOptionnels() {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<string[]>([]);
  const [periodicite, setPeriodicite] = useState<'mensuel' | 'annuel'>('annuel');

  // Le retour de Stripe repasse par cette page : merci ou annulé.
  const retour = useMemo(() => new URLSearchParams(window.location.search).get('modules'), []);

  const modulesQuery = useQuery({ queryKey: ['admin-modules'], queryFn: fetchModulesAdmin });

  const commande = useMutation({
    mutationFn: () => commanderModulesAdmin(selection, periodicite),
    onSuccess: (data: any) => {
      if (data?.url) {
        // Direction Stripe : le paiement se fait sur la boutique de la
        // plateforme, l'activation reviendra par le webhook.
        window.location.href = data.url;
      }
    },
  });

  // Garder ou écarter un module déjà ouvert. Le module se ferme, et quitte
  // les prochaines échéances — les deux vont ensemble.
  const garder = useMutation({
    mutationFn: ({ code, garder }: { code: string; garder: boolean }) =>
      garderModuleAdmin(code, garder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules'] });
      // Le menu de gauche se règle sur les mêmes options : sans cela, un
      // module fermé garderait son entrée jusqu'au prochain rechargement.
      queryClient.invalidateQueries({ queryKey: ['personnalisation'] });
      queryClient.invalidateQueries({ queryKey: ['admin-renouvellement'] });
    },
  });

  if (modulesQuery.isPending) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const donnees = modulesQuery.data ?? {};
  const modules: Module[] = donnees.modules ?? [];
  const essai: boolean = donnees.essai === true;
  const gratuiteFin: string | null = donnees.gratuiteFin ?? null;

  if (modulesQuery.isError || !donnees.catalogueJoint || modules.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          Le catalogue des modules est momentanément indisponible : réessayez dans quelques
          minutes, ou passez par{' '}
          <a href="https://shop.gest-asso.com" target="_blank" rel="noopener" className="underline font-medium">
            shop.gest-asso.com
          </a>.
        </p>
      </div>
    );
  }

  const cochables = modules.filter((m) => m.pilotable && m.disponible && !m.inclus);
  const clePrix = periodicite === 'annuel' ? 'prixAnnuel' : 'prixMensuel';
  const total = modules
    .filter((m) => selection.includes(m.code))
    .reduce((somme, m) => somme + (m[clePrix] as number), 0);

  const basculer = (code: string) =>
    setSelection((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  return (
    <div className="space-y-4">
      {retour === 'merci' && (
        <div className="rounded-xl border border-green-200 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200 px-4 py-3 text-sm flex items-start gap-2">
          <Check className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Merci ! Dès que Stripe confirme le paiement, les modules s'activent d'eux-mêmes —
            rechargez la page dans une minute si besoin.
          </p>
        </div>
      )}
      {retour === 'annule' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 px-4 py-3 text-sm">
          Paiement annulé : rien n'a été débité, la sélection reste à refaire.
        </div>
      )}

      {/* Pendant l'essai ou une gratuité, tout est ouvert : le bloc le dit
          pour qu'un « Inclus » général ne passe pas pour un acquis. */}
      {(essai || gratuiteFin) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 px-4 py-3 text-sm flex items-start gap-2">
          <Gift className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            {essai ? (
              <>
                <strong>Mode essai :</strong> tous les modules sont ouverts pour les découvrir.
                Au passage en production, seuls les modules souscrits resteront actifs.
              </>
            ) : (
              <>
                <strong>Période offerte :</strong> tous les modules sont ouverts jusqu'au{' '}
                {dateFr(gratuiteFin + 'T00:00:00')}. Au-delà, seuls
                les modules souscrits resteront actifs.
              </>
            )}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {modules.map((m) => {
          const Icone = ICONES[m.icone] ?? Puzzle;
          const coche = selection.includes(m.code);
          const commandable = m.pilotable && m.disponible && !m.inclus;

          return (
            <div
              key={m.code}
              className={`rounded-xl border p-4 flex flex-col gap-2 ${
                m.refuse
                  ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40'
                  : m.inclus
                    ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10'
                    : coche
                      ? 'border-primary-400 bg-primary-50/50 dark:border-primary-600 dark:bg-primary-900/10'
                      : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icone
                    className={`w-5 h-5 shrink-0 ${
                      m.refuse
                        ? 'text-gray-400 dark:text-gray-500'
                        : 'text-primary-700 dark:text-primary-400'
                    }`}
                  />
                  <span
                    className={`font-semibold ${
                      m.refuse
                        ? 'text-gray-500 dark:text-gray-400'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {m.nom}
                  </span>
                </div>
                {m.refuse ? (
                  <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap inline-flex items-center gap-1">
                    <MinusCircle className="w-3 h-3" />
                    Écarté
                  </span>
                ) : m.inclus ? (
                  <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 whitespace-nowrap">
                    {m.souscrit ? 'Souscrit' : 'Inclus'}
                  </span>
                ) : !m.disponible ? (
                  <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 whitespace-nowrap">
                    À venir
                  </span>
                ) : null}
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">{m.description}</p>

              {/* Ce qui arrive au module, dit avant qu'on touche à
                  l'interrupteur — pas découvert sur la facture suivante. */}
              {m.refuse ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {m.payeJusquAuTerme
                    ? 'Réglé jusqu\'à votre échéance : il reste utilisable jusque-là, puis s\'éteint. Il ne figure plus sur vos prochaines échéances.'
                    : 'Fermé, et absent de vos prochaines échéances.'}
                </p>
              ) : m.refusable ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Vous ne vous en servez pas ? Mettez-le sur « Non » : il quitte votre espace et vos
                  prochaines échéances.
                </p>
              ) : null}

              <div className="flex items-center justify-between gap-3">
                <span
                  className={`text-sm ${
                    m.refuse
                      ? 'text-gray-400 dark:text-gray-500 line-through'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <strong>{euros(m.prixMensuel)}</strong>/mois ou{' '}
                  <strong>{euros(m.prixAnnuel)}</strong>/an
                </span>

                {m.refusable ? (
                  // Garder ou écarter : la décision porte sur la facture à
                  // venir autant que sur l'écran.
                  <Interrupteur
                    actif={!m.refuse}
                    occupe={garder.isPending}
                    libelle={`Garder le module ${m.nom}`}
                    onChange={() => garder.mutate({ code: m.code, garder: m.refuse })}
                  />
                ) : commandable ? (
                  // Le oui/non d'activation : cocher, c'est mettre au panier.
                  <Interrupteur
                    actif={coche}
                    libelle={`Ajouter le module ${m.nom}`}
                    onChange={() => basculer(m.code)}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Le règlement : visible dès qu'un module est coché. Le total engageant
          se recalcule côté boutique — celui-ci n'est que l'annonce. */}
      {cochables.length > 0 && selection.length > 0 && (
        <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{selection.length}</span> module
              {selection.length > 1 ? 's' : ''} sélectionné{selection.length > 1 ? 's' : ''} —
              montant à régler :{' '}
              <span className="font-bold text-gray-900 dark:text-gray-100">
                {euros(total)}
                {periodicite === 'annuel' ? '/an' : '/mois'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={periodicite}
                onChange={(e) => setPeriodicite(e.target.value as 'mensuel' | 'annuel')}
                className="input-field !w-auto text-sm"
              >
                <option value="annuel">Paiement annuel</option>
                <option value="mensuel">Paiement mensuel</option>
              </select>
              <button
                type="button"
                onClick={() => commande.mutate()}
                disabled={commande.isPending}
                className="btn-primary flex items-center gap-2 whitespace-nowrap"
              >
                {commande.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                Valider et régler
              </button>
            </div>
          </div>
          {commande.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {(commande.error as any)?.message ?? 'La commande a échoué : réessayez.'}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Le règlement se fait par carte sur la boutique de la plateforme (Stripe). Les modules
            s'activent automatiquement dès le paiement confirmé.
          </p>
        </div>
      )}
    </div>
  );
}
